// src/MotionNumber.tsx
import * as React3 from "react";
import {
  addScaleCorrector,
  MotionConfig,
  easeOut,
  MotionConfigContext as MotionConfigContext2,
  useIsPresent as useIsPresent2,
  useAnimate
} from "framer-motion";

// src/JustifiedAnimatePresence.tsx
import * as React2 from "react";
import { AnimatePresence } from "framer-motion";

// src/PopChildRight.tsx
import { MotionConfigContext, useIsPresent } from "framer-motion";
import * as React from "react";
import { useRef, useInsertionEffect, useId, useContext } from "react";
var PopChildMeasure = class extends React.Component {
  getSnapshotBeforeUpdate(prevProps) {
    const element = this.props.childRef.current;
    if (element && prevProps.isPresent && !this.props.isPresent) {
      const size = this.props.sizeRef.current;
      size.height = element.offsetHeight || 0;
      size.width = element.offsetWidth || 0;
      size.top = element.offsetTop;
      size.right = ((element.offsetParent instanceof HTMLElement ? element.offsetParent : null)?.offsetWidth ?? 0) - element.offsetWidth - element.offsetLeft;
    }
    return null;
  }
  /**
   * Required with getSnapshotBeforeUpdate to stop React complaining.
   */
  componentDidUpdate() {
  }
  render() {
    return this.props.children;
  }
};
function PopChildRight({ children }) {
  const isPresent = useIsPresent();
  const id = useId();
  const ref = useRef(null);
  const size = useRef({
    width: 0,
    height: 0,
    top: 0,
    right: 0
  });
  const { nonce } = useContext(MotionConfigContext);
  useInsertionEffect(() => {
    const { width, height, top, right } = size.current;
    if (isPresent || !ref.current || !width || !height) return;
    ref.current.dataset.motionNumberPopId = id;
    const style = document.createElement("style");
    if (nonce) style.nonce = nonce;
    document.head.appendChild(style);
    if (style.sheet) {
      style.sheet.insertRule(`
          [data-motion-number-pop-id="${id}"] {
            position: absolute !important;
            width: ${width}px !important;
            height: ${height}px !important;
            top: ${top}px !important;
            right: ${right}px !important;
          }
        `);
    }
    return () => {
      document.head.removeChild(style);
    };
  }, [isPresent]);
  return /* @__PURE__ */ React.createElement(PopChildMeasure, { isPresent, childRef: ref, sizeRef: size }, React.cloneElement(children, { ref }));
}

// src/JustifiedAnimatePresence.tsx
var getChildKey = (child) => child.key ?? "";
function JustifiedAnimatePresence({
  justify = "left",
  children: _children,
  mode,
  ...rest
}) {
  const popRight = mode === "popLayout" && justify === "right";
  const children = Array.isArray(_children) ? _children : [_children];
  return /* @__PURE__ */ React2.createElement(AnimatePresence, { ...rest, mode: popRight ? "sync" : mode }, popRight ? children.map((child) => /* @__PURE__ */ React2.createElement(PopChildRight, { key: getChildKey(child) }, child)) : children);
}

// src/MotionNumber.tsx
addScaleCorrector({
  "--motion-number-scale-x-correct": {
    correct: (_, { treeScale, projectionDelta }) => projectionDelta.x.scale * treeScale.x
  }
});
function getWidthInEm(element) {
  const { width, fontSize } = getComputedStyle(element);
  return `${parseFloat(width) / parseFloat(fontSize)}em`;
}
function useIsInitialRender() {
  const [initialRender, setInitialRender] = React3.useState(true);
  React3.useEffect(() => {
    setInitialRender(false);
  }, []);
  return initialRender;
}
var formatToParts = (value, { locales, format }) => {
  const formatter = new Intl.NumberFormat(locales, format);
  const parts = formatter.formatToParts(value);
  const pre = [];
  const _integer = [];
  const fraction = [];
  const post = [];
  const counts = {};
  const generateKey = (type) => {
    if (!counts[type]) counts[type] = 0;
    return `${type}:${counts[type]++}`;
  };
  let formatted = "";
  let seenInteger = false, seenDecimal = false;
  for (const part of parts) {
    formatted += part.value;
    const type = part.type === "minusSign" || part.type === "plusSign" ? "sign" : part.type;
    switch (type) {
      case "integer":
        seenInteger = true;
        _integer.push(...part.value.split("").map((d) => ({ type, value: parseInt(d) })));
        break;
      case "group":
        _integer.push({ type, value: part.value });
        break;
      case "decimal":
        seenDecimal = true;
        fraction.push({ type, value: part.value, key: generateKey(type) });
        break;
      case "fraction":
        fraction.push(
          ...part.value.split("").map((d) => ({ type, value: parseInt(d), key: generateKey(type) }))
        );
        break;
      // case 'nan':
      // case 'infinity':
      // 	// TODO: handle these
      // 	break
      // case 'exponentSeparator':
      // 	break
      // case 'exponentMinusSign':
      // case 'exponentInteger':
      // 	break
      default:
        ;
        (seenInteger || seenDecimal ? post : pre).push({
          type,
          value: part.value,
          key: generateKey(type)
        });
    }
  }
  const integer = [];
  for (let i = _integer.length - 1; i >= 0; i--) {
    integer.unshift({ ..._integer[i], key: generateKey(_integer[i].type) });
  }
  return { pre, integer, fraction, post, formatted };
};
var DEFAULT_TRANSITION = {
  // We use keyframes and times so the opacity/exit animations can last
  // as long as the layout animation, so Framer Motion doesn't have to
  // remove exiting elements until the layout animation is done.
  // This worked better in testing than safeToRemove() from usePresence()
  opacity: { duration: 1, ease: easeOut, times: [0, 0.5] },
  // perceptual duration of 0.5s
  layout: { type: "spring", duration: 1, bounce: 0 },
  y: { type: "spring", duration: 1, bounce: 0 }
};
var maskHeight = "var(--mask-height, 0.15em)";
var maskWidth = "var(--mask-width, 0.5em)";
var correctedMaskWidth = `calc(${maskWidth} / var(--motion-number-scale-x-correction, 1))`;
var cornerGradient = `#000 0, transparent 71%`;
var mask = (
  // Horizontal:
  `linear-gradient(to right, transparent 0, #000 ${correctedMaskWidth}, #000 calc(100% - ${correctedMaskWidth}), transparent),linear-gradient(to bottom, transparent 0, #000 ${maskHeight}, #000 calc(100% - ${maskHeight}), transparent 100%),radial-gradient(at bottom right, ${cornerGradient}),radial-gradient(at bottom left, ${cornerGradient}), radial-gradient(at top left, ${cornerGradient}), radial-gradient(at top right, ${cornerGradient})`
);
var maskSize = `100% calc(100% - ${maskHeight} * 2),calc(100% - ${correctedMaskWidth} * 2) 100%,${correctedMaskWidth} ${maskHeight},${correctedMaskWidth} ${maskHeight},${correctedMaskWidth} ${maskHeight},${correctedMaskWidth} ${maskHeight}`;
var MotionNumberContext = React3.createContext({
  transition: DEFAULT_TRANSITION
});
var useMotion = () => {
  const { motion } = React3.useContext(MotionNumberContext);
  return motion;
};
var MotionNumber = React3.forwardRef(function MotionNumber2({
  value,
  locales,
  format,
  transition: _transition,
  before,
  first,
  last,
  after,
  style,
  motion,
  ...rest
}, ref) {
  const parts = React3.useMemo(
    () => formatToParts(value, { locales, format }),
    [value, locales, format]
  );
  const { pre, integer, fraction, post, formatted } = parts;
  const maskedRef = React3.useRef(null);
  const appliedScaleSetter = React3.useRef(false);
  React3.useEffect(() => {
    if (appliedScaleSetter.current || !maskedRef.current?.style) return;
    Object.defineProperty(maskedRef.current.style, "--motion-number-scale-x-correct", {
      set(v) {
        return this.setProperty("--motion-number-scale-x-correction", v);
      }
    });
    appliedScaleSetter.current = true;
  }, []);
  const { transition: motionConfigTransition } = React3.useContext(MotionConfigContext2);
  const transition = _transition ?? motionConfigTransition ?? DEFAULT_TRANSITION;
  const [_updateCount, setUpdateCount] = React3.useState(0);
  const forceUpdate = React3.useCallback(() => {
    setUpdateCount(_updateCount + 1);
  }, [_updateCount]);
  const parent = React3.useContext(MotionNumberContext);
  const context = React3.useMemo(
    () => ({
      forceUpdate: parent.forceUpdate ?? forceUpdate,
      motion,
      transition
    }),
    [parent, forceUpdate, motion, transition]
  );
  return /* @__PURE__ */ React3.createElement(MotionNumberContext.Provider, { value: context }, /* @__PURE__ */ React3.createElement(MotionConfig, { transition }, before?.(), /* @__PURE__ */ React3.createElement(
    motion.span,
    {
      ...rest,
      ref,
      layout: true,
      "data-motion-number": "",
      style: {
        lineHeight: 1,
        // make this one easy to override
        ...style,
        display: "inline-flex",
        isolation: "isolate",
        // so number can be underneath first/last
        whiteSpace: "nowrap"
      }
    },
    first?.(),
    /* @__PURE__ */ React3.createElement(
      motion.span,
      {
        layout: true,
        style: {
          display: "inline-flex",
          direction: "ltr",
          // I think this is needed b/c numbers are always LTR?
          isolation: "isolate",
          // so number can be underneath pre/post
          position: "relative",
          zIndex: -1,
          // so the whole number is under any first/last
          userSelect: "none",
          // I think adding this to the parent then undoing it on the selectable one might work a little better
          pointerEvents: "none"
        }
      },
      /* @__PURE__ */ React3.createElement(
        "span",
        {
          style: {
            position: "absolute",
            left: 0,
            padding: `${maskHeight} 0`,
            pointerEvents: "all",
            userSelect: "text",
            color: "transparent",
            overflow: "clip",
            minWidth: "100%",
            top: 0,
            zIndex: -50
          }
        },
        formatted
      ),
      /* @__PURE__ */ React3.createElement(
        Section,
        {
          "data-motion-number-part": "pre",
          style: { padding: `calc(${maskHeight}/2) 0` },
          "aria-hidden": true,
          inert: "",
          justify: "right",
          mode: "popLayout",
          parts: pre
        }
      ),
      /* @__PURE__ */ React3.createElement(
        motion.span,
        {
          layout: true,
          ref: maskedRef,
          "aria-hidden": true,
          inert: "",
          style: {
            display: "inline-flex",
            // Activates the scale correction, which gets stored in --motion-number-scale-x-correction
            "--motion-number-scale-x-correct": 1,
            margin: `0 calc(-1*${maskWidth})`,
            padding: `calc(${maskHeight}/2) ${maskWidth}`,
            position: "relative",
            // for zIndex
            zIndex: -1,
            // should be underneath everything else
            overflow: "clip",
            // important so it doesn't affect page layout
            // Prefixed properties have better support than unprefixed ones:
            WebkitMaskImage: mask,
            WebkitMaskSize: maskSize,
            WebkitMaskPosition: "center, center, top left, top right, bottom right, bottom left",
            WebkitMaskRepeat: "no-repeat"
          }
        },
        /* @__PURE__ */ React3.createElement(Section, { "data-motion-number-part": "integer", justify: "right", parts: integer }),
        /* @__PURE__ */ React3.createElement(Section, { "data-motion-number-part": "fraction", layout: "position", parts: fraction })
      ),
      /* @__PURE__ */ React3.createElement(
        Section,
        {
          "data-motion-number-part": "post",
          style: { padding: `calc(${maskHeight}/2) 0` },
          "aria-hidden": true,
          layout: "position",
          inert: "",
          mode: "popLayout",
          parts: post
        }
      )
    ),
    last?.()
  ), after?.()));
});
var MotionNumber_default = MotionNumber;
var SectionContext = React3.createContext({
  justify: "left"
});
var Section = React3.forwardRef(function Section2({ parts, justify = "left", mode, style, ...rest }, _ref) {
  const ref = React3.useRef(null);
  React3.useImperativeHandle(_ref, () => ref.current, []);
  const { forceUpdate } = React3.useContext(MotionNumberContext);
  const motion = useMotion();
  const context = React3.useMemo(() => ({ justify }), [justify]);
  const measuredRef = React3.useRef(null);
  const isInitialRender = useIsInitialRender();
  const [width, setWidth] = React3.useState();
  React3.useEffect(() => {
    if (!measuredRef.current) return;
    if (isInitialRender) {
      if (ref.current) ref.current.style.width = getWidthInEm(measuredRef.current);
      return;
    }
    const undos = Array.from(measuredRef.current.children).map((child) => {
      if (!(child instanceof HTMLElement)) return;
      if (child.dataset.motionNumberState === "exiting") {
        const next = child.nextSibling;
        child.remove();
        return () => {
          measuredRef.current?.insertBefore(child, next);
        };
      }
      const newWidth = targetWidths.get(child);
      if (!newWidth) return;
      const oldWidth = child.style.width;
      child.style.width = newWidth;
      return () => {
        child.style.width = oldWidth;
      };
    });
    setWidth(getWidthInEm(measuredRef.current));
    for (let i = undos.length - 1; i >= 0; i--) undos[i]?.();
    forceUpdate?.();
  }, [parts.map((p) => p.value).join("")]);
  return /* @__PURE__ */ React3.createElement(SectionContext.Provider, { value: context }, /* @__PURE__ */ React3.createElement(
    motion.span,
    {
      ...rest,
      ref,
      style: {
        ...style,
        display: "inline-flex",
        justifyContent: justify,
        width
      }
    },
    /* @__PURE__ */ React3.createElement(
      "span",
      {
        ref: measuredRef,
        style: {
          display: "inline-flex",
          justifyContent: "inherit",
          position: "relative"
          // needed for AnimatePresent popLayout
        }
      },
      "\u200B",
      /* @__PURE__ */ React3.createElement(JustifiedAnimatePresence, { mode, justify, initial: false }, parts.map(
        (part) => part.type === "integer" || part.type === "fraction" ? /* @__PURE__ */ React3.createElement(
          Digit,
          {
            key: part.key,
            initial: { opacity: 0 },
            animate: { opacity: [null, 1] },
            exit: { opacity: [null, 0] },
            value: part.value,
            initialValue: isInitialRender ? void 0 : 0
          }
        ) : /* @__PURE__ */ React3.createElement(
          Sym,
          {
            key: part.type === "literal" ? `${part.key}:${part.value}` : part.key,
            type: part.type,
            partKey: part.key,
            initial: { opacity: 0 },
            animate: { opacity: [null, 1] },
            exit: { opacity: [null, 0] }
          },
          part.value
        )
      ))
    )
  ));
});
var targetWidths = /* @__PURE__ */ new WeakMap();
var Digit = React3.forwardRef(function Digit2({ value: _value, initialValue: _initialValue = _value, ...rest }, _ref) {
  const motion = useMotion();
  const { transition } = React3.useContext(MotionNumberContext);
  const initialValue = React3.useRef(_initialValue).current;
  const isInitialRender = useIsInitialRender();
  const ref = React3.useRef(null);
  React3.useImperativeHandle(_ref, () => ref.current, []);
  const [scope, animate] = useAnimate();
  const numberRefs = React3.useRef(new Array(10));
  const isPresent = useIsPresent2();
  const value = isPresent ? _value : 0;
  React3.useLayoutEffect(() => {
    if (numberRefs.current[initialValue])
      scope.current.style.width = getWidthInEm(numberRefs.current[initialValue]);
  }, []);
  const prevValue = React3.useRef(_initialValue);
  React3.useLayoutEffect(() => {
    if (value === prevValue.current) return;
    const box = scope.current.getBoundingClientRect();
    const refBox = ref.current?.getBoundingClientRect();
    animate(
      scope.current,
      {
        // Using a number seems to ensure Framr Motion ends with "none", which we want:
        // Add the offset between the top of the inner and outer elements to account for
        // any current animation state:
        y: [box.height * (value - prevValue.current) + (box.top - (refBox?.top ?? box.top)), 0]
      },
      transition
    );
    return () => {
      prevValue.current = value;
    };
  }, [value]);
  const { forceUpdate } = React3.useContext(MotionNumberContext);
  const [width, setWidth] = React3.useState();
  React3.useEffect(() => {
    if (isInitialRender && initialValue === value) return;
    if (!numberRefs.current[value]) return;
    const w = getWidthInEm(numberRefs.current[value]);
    if (ref.current) targetWidths.set(ref.current, w);
    setWidth(w);
    forceUpdate?.();
  }, [value]);
  const renderNumber = (i) => /* @__PURE__ */ React3.createElement(
    "span",
    {
      key: i,
      style: {
        display: "inline-block",
        padding: `calc(${maskHeight}/2) 0`
      },
      ref: (r) => void (numberRefs.current[i] = r)
    },
    i
  );
  return /* @__PURE__ */ React3.createElement(
    motion.span,
    {
      ...rest,
      ref,
      layout: "position",
      "data-motion-number-state": isPresent ? void 0 : "exiting",
      "data-motion-number-part": "digit",
      "data-motion-number-value": value,
      style: {
        display: "inline-flex",
        justifyContent: "center",
        width
      }
    },
    /* @__PURE__ */ React3.createElement(motion.span, { layout: "position", style: { display: "inline-flex", justifyContent: "center" } }, /* @__PURE__ */ React3.createElement(
      motion.span,
      {
        ref: scope,
        style: {
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative"
        }
      },
      value !== 0 && /* @__PURE__ */ React3.createElement(
        "span",
        {
          style: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "absolute",
            bottom: `100%`,
            left: 0,
            width: "100%"
          }
        },
        new Array(value).fill(null).map((_, i) => renderNumber(i))
      ),
      renderNumber(value),
      value !== 9 && /* @__PURE__ */ React3.createElement(
        "span",
        {
          style: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "absolute",
            top: `100%`,
            left: 0,
            width: "100%"
          }
        },
        new Array(9 - value).fill(null).map((_, i) => renderNumber(value + i + 1))
      )
    ))
  );
});
var Sym = React3.forwardRef(function Sym2({ partKey: key, type, children: value, ...rest }, ref) {
  const motion = useMotion();
  const isPresent = useIsPresent2();
  const { justify } = React3.useContext(SectionContext);
  return /* @__PURE__ */ React3.createElement(
    motion.span,
    {
      ...rest,
      ref,
      "data-motion-number-state": isPresent ? void 0 : "exiting",
      "data-motion-number-part": type,
      "data-motion-number-value": value,
      style: {
        display: "inline-flex",
        justifyContent: justify,
        padding: `calc(${maskHeight}/2) 0`,
        // match digits
        position: "relative"
        // needed for AnimatePresent popLayout
      },
      layout: "position"
    },
    /* @__PURE__ */ React3.createElement(JustifiedAnimatePresence, { mode: "popLayout", justify, initial: false }, /* @__PURE__ */ React3.createElement(
      motion.span,
      {
        key: value,
        layout: justify === "right" ? "position" : false,
        ref,
        initial: { opacity: 0 },
        animate: { opacity: [null, 1] },
        exit: { opacity: [null, 0] },
        style: {
          display: "inline-block",
          whiteSpace: "pre"
          // some symbols are spaces or thin spaces
        }
      },
      value
    ))
  );
});

export {
  DEFAULT_TRANSITION,
  MotionNumberContext,
  MotionNumber_default
};
